import {isBoolean, isEmpty} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';

import {
    TwilioRoom, TwilioVideo, TwilioScreenSharing, TwilioAudio, KurentoRoom, KurentoVideo, KurentoAudio, KurentoScreenSharing,
    TwilioVideoConference
} from 'components';
import {VideoPinParticipant, WebRTCServerSource} from 'enums';
import {AudioVideoView, ScreenSharingActivityStatus, SessionActivity} from 'interfaces';
import {MediaDevicesConstraints, VirtualBackgroundConfig, WebRTCConnectDetails} from 'types';
import {sessionUtils} from 'utils';

type RoomProps = {
    participantId: string;
    roomName: string;
    participantIds?: string[];
    hidden?: boolean;
    mediaConstraints?: MediaDevicesConstraints;
    videoDevices?: MediaDeviceInfo[];
    audioDevices?: MediaDeviceInfo[];
    audioEnabled?: boolean;
    videoEnabled?: boolean;
    videoMinimized?: boolean;
    videoPinParticipant?: VideoPinParticipant;
    fullScreenVideoDefault?: boolean;
    fullScreenVideo?: boolean;
    isScreenSharingPresenter?: boolean;
    ignoreErrorDialog?: boolean;
    audioActivity?: SessionActivity;
    audioVideoActivity?: SessionActivity;
    avatarActivity?: SessionActivity;
    screenSharingActivity?: SessionActivity;
    screenSharingMinimized?: boolean;
    screenSharingActivityStatus?: ScreenSharingActivityStatus;
    overLocalVideoContent?: React.ReactNode;
    overRemoteVideoContent?: React.ReactNode;
    parentRect?: {
        width: number,
        height: number
    };
    screenSharingMessageClassName?: string;
    screenSharingConfirmRequired?: boolean;
    videoWrapperClassName?: string;
    virtualBackgroundConfig?: VirtualBackgroundConfig;
    defaultView?: AudioVideoView;
    pinnedParticipantId?: string;
    audioVideoAsColumn?: boolean;
    getTwilioTokenFn: () => Promise<{token: string}>;
    getKurentoTokenFn: () => Promise<{token: string}>;
    onToggleFullScreenVideo?: (value: boolean) => void;
    onLocalNetworkQualityLevelChange?: (level: number) => void;
    onRemoteNetworkQualityLevelChange?: (level: number) => void;
    renderOverParticipantContent?: (participantId: string) => React.ReactNode;
    onEnumerateMediaDevices: (devices: MediaDeviceInfo[]) => void;
    onEnumerateTracks: (tracks: MediaStreamTrack[]) => void;
    notifyWebRTCStartSuccess?: (source: WebRTCServerSource, details?: WebRTCConnectDetails) => void;
    notifyWebRTCStartFailed?: (source: WebRTCServerSource, details: WebRTCConnectDetails) => void;
    onStartScreenSharing?: () => void;
    onStopScreenSharing?: (error?: any) => void;
    onWebRTCConnectError?: () => void;
}

export const SessionWebRTCRoom: React.FC<RoomProps> = ({
    participantId, roomName, participantIds, hidden, mediaConstraints, audioActivity, avatarActivity, audioDevices, audioEnabled,
    audioVideoActivity, videoDevices, videoMinimized, fullScreenVideoDefault, fullScreenVideo, videoPinParticipant, virtualBackgroundConfig,
    videoEnabled, overLocalVideoContent, overRemoteVideoContent, isScreenSharingPresenter, ignoreErrorDialog, screenSharingConfirmRequired,
    screenSharingActivityStatus, screenSharingActivity, parentRect, screenSharingMessageClassName, videoWrapperClassName, defaultView,
    pinnedParticipantId, audioVideoAsColumn, screenSharingMinimized,
    onLocalNetworkQualityLevelChange, onRemoteNetworkQualityLevelChange, getTwilioTokenFn, getKurentoTokenFn, renderOverParticipantContent,
    onEnumerateMediaDevices, notifyWebRTCStartSuccess, notifyWebRTCStartFailed, onToggleFullScreenVideo, onStartScreenSharing,
    onStopScreenSharing, onWebRTCConnectError, onEnumerateTracks
}) => {

    const filterBySource = useCallback((source: WebRTCServerSource) =>
        sessionUtils.filterWebRTCActivitiesBySource([audioActivity, audioVideoActivity, avatarActivity, screenSharingActivity], source),
    [audioActivity, audioVideoActivity, avatarActivity, screenSharingActivity]);

    const twilioWebRTCRoomActivities = useMemo(() => filterBySource(WebRTCServerSource.Twilio), [filterBySource]);
    const internalWebRTCRoomActivities = useMemo(() => filterBySource(WebRTCServerSource.Internal), [filterBySource]);

    const hasTwilioWebRTCRoomActivities = useMemo(() => twilioWebRTCRoomActivities.length > 0, [twilioWebRTCRoomActivities.length]);
    const hasInternalWebRTCRoomActivities = useMemo(() => internalWebRTCRoomActivities.length > 0, [internalWebRTCRoomActivities.length]);

    const onEnumerateMediaDevicesWrapper = useCallback((devices: MediaDeviceInfo[]) => {
        if (devices.length > 0 || !audioActivity && !audioVideoActivity) {
            onEnumerateMediaDevices(devices);
        }
    }, [audioActivity, audioVideoActivity, onEnumerateMediaDevices]);

    const VideoWrapperTag = useMemo(() => videoWrapperClassName ? 'div' : Fragment, [videoWrapperClassName]);
    const videoWrapperTagProps = useMemo(() => ({...videoWrapperClassName && {className: videoWrapperClassName}}),
        [videoWrapperClassName]);

    if (hidden || !roomName || (!hasTwilioWebRTCRoomActivities && !hasInternalWebRTCRoomActivities)) {
        return null;
    }

    return (
        <Fragment>
            {hasTwilioWebRTCRoomActivities &&
                <TwilioRoom
                    mediaConstraints={mediaConstraints}
                    roomName={roomName}
                    ignoreErrorDialog={ignoreErrorDialog}
                    getTokenFn={getTwilioTokenFn}
                    onEnumerateTracks={onEnumerateTracks}
                    onEnumerateMediaDevices={onEnumerateMediaDevicesWrapper}
                    onWebRTCStartSuccess={(details) => notifyWebRTCStartSuccess(WebRTCServerSource.Twilio, details)}
                    onWebRTCStartFailed={(details) => notifyWebRTCStartFailed(WebRTCServerSource.Twilio, details)}
                >
                    {({room}) => (
                        <Fragment>
                            {twilioWebRTCRoomActivities.includes(audioActivity) && !isEmpty(audioDevices) &&
                                <TwilioAudio
                                    room={room}
                                    mediaConstraints={mediaConstraints}
                                    isAudioEnabled={audioEnabled}
                                    onLocalNetworkQualityLevelChange={onLocalNetworkQualityLevelChange}
                                    onRemoteNetworkQualityLevelChange={onRemoteNetworkQualityLevelChange}
                                />}
                            {twilioWebRTCRoomActivities.includes(audioVideoActivity) && !isEmpty(videoDevices) &&
                                <VideoWrapperTag {...videoWrapperTagProps}>
                                    {defaultView === AudioVideoView.Conference ?
                                        <TwilioVideoConference
                                            room={room}
                                            participantIds={[participantId, ...participantIds]}
                                            mediaConstraints={mediaConstraints}
                                            isAudioEnabled={audioEnabled}
                                            isVideoEnabled={videoEnabled}
                                            virtualBackgroundConfig={virtualBackgroundConfig}
                                            pinnedParticipantId={pinnedParticipantId}
                                            asColumn={audioVideoAsColumn}
                                            renderOverParticipantContent={renderOverParticipantContent}
                                            onLocalNetworkQualityLevelChange={onLocalNetworkQualityLevelChange}
                                            onRemoteNetworkQualityLevelChange={onRemoteNetworkQualityLevelChange}
                                        /> :
                                        <TwilioVideo
                                            room={room}
                                            pinParticipant={videoPinParticipant}
                                            mediaConstraints={mediaConstraints}
                                            mini={videoMinimized}
                                            parentRect={parentRect}
                                            skipAutoDetectFullScreen={isBoolean(fullScreenVideoDefault)}
                                            isFullScreen={fullScreenVideo}
                                            isAudioEnabled={audioEnabled}
                                            isVideoEnabled={videoEnabled}
                                            virtualBackgroundConfig={virtualBackgroundConfig}
                                            toggleFullScreen={onToggleFullScreenVideo}
                                            overLocalVideoContent={overLocalVideoContent}
                                            overRemoteVideoContent={overRemoteVideoContent}
                                            onLocalNetworkQualityLevelChange={onLocalNetworkQualityLevelChange}
                                            onRemoteNetworkQualityLevelChange={onRemoteNetworkQualityLevelChange}
                                        />}
                                </VideoWrapperTag>}
                            {twilioWebRTCRoomActivities.includes(screenSharingActivity) &&
                                <TwilioScreenSharing
                                    messageClassName={screenSharingMessageClassName}
                                    room={room}
                                    mini={screenSharingMinimized}
                                    isPresenter={isScreenSharingPresenter}
                                    status={screenSharingActivityStatus}
                                    confirmRequired={screenSharingConfirmRequired}
                                    columnLayout={audioVideoAsColumn}
                                    onStart={onStartScreenSharing}
                                    onStop={onStopScreenSharing}
                                />}
                        </Fragment>
                    )}
                </TwilioRoom>}

            {hasInternalWebRTCRoomActivities &&
                <KurentoRoom
                    mediaConstraints={mediaConstraints}
                    ignoreErrorDialog={ignoreErrorDialog}
                    getTokenFn={getKurentoTokenFn}
                    onEnumerateMediaDevices={onEnumerateMediaDevicesWrapper}
                    onEnumerateTracks={onEnumerateTracks}
                    onWebRTCStartSuccess={(details) => notifyWebRTCStartSuccess(WebRTCServerSource.Internal, details)}
                    onWebRTCStartFailed={(details) => notifyWebRTCStartFailed(WebRTCServerSource.Internal, details)}
                >
                    {({room}) => (
                        <Fragment>
                            {internalWebRTCRoomActivities.includes(audioActivity) && !isEmpty(audioDevices) &&
                                <KurentoAudio
                                    room={room}
                                    isAudioEnabled={audioEnabled}
                                    mediaConstraints={mediaConstraints}
                                    onConnectError={onWebRTCConnectError}
                                />}
                            {internalWebRTCRoomActivities.includes(audioVideoActivity) && !isEmpty(videoDevices) &&
                                <VideoWrapperTag {...videoWrapperTagProps}>
                                    <KurentoVideo
                                        participantId={participantId}
                                        pinParticipant={videoPinParticipant}
                                        room={room}
                                        mini={videoMinimized}
                                        parentRect={parentRect}
                                        skipAutoDetectFullScreen={isBoolean(fullScreenVideoDefault)}
                                        isFullScreen={fullScreenVideo}
                                        isAudioEnabled={audioEnabled}
                                        isVideoEnabled={videoEnabled}
                                        overLocalVideoContent={overLocalVideoContent}
                                        overRemoteVideoContent={overRemoteVideoContent}
                                        mediaConstraints={mediaConstraints}
                                        toggleFullScreen={onToggleFullScreenVideo}
                                        onConnectError={onWebRTCConnectError}
                                    />
                                </VideoWrapperTag>}
                            {internalWebRTCRoomActivities.includes(screenSharingActivity) &&
                                <KurentoScreenSharing
                                    messageClassName={screenSharingMessageClassName}
                                    room={room}
                                    mini={screenSharingMinimized}
                                    isPresenter={isScreenSharingPresenter}
                                    status={screenSharingActivityStatus}
                                    confirmRequired={screenSharingConfirmRequired}
                                    onStart={onStartScreenSharing}
                                    onStop={onStopScreenSharing}
                                />}
                        </Fragment>
                    )}
                </KurentoRoom>}
        </Fragment>
    );
};